import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
  Box,
  Heading,
  Text,
  HStack,
  Flex,
  Card,
  CardBody,
  useColorModeValue,
  Icon,
  Button,
  Badge,
  Image,
  Skeleton,
  Tabs,
  TabList,
  Tab,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
} from '@chakra-ui/react';
import { 
  FaBookOpen,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaBookmark,
  FaRegBookmark,
  FaStar,
  FaChevronRight,
  FaChevronLeft,
  FaGlobe,
  FaNewspaper,
  FaBook,
  FaMapMarkerAlt,
  FaIndustry,
  FaEllipsisV,
  FaThumbsUp,
  FaThumbsDown,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getReadingItems, updateReadingItem, markReadingItemAsRead, fetchReadingItemPreview } from '../services/playbookService';
import { logger } from '../utils/logger';

// Source icon mapping
const getSourceIcon = (source) => {
  const sourceLower = source?.toLowerCase() || '';
  if (sourceLower.includes('forbes')) return FaNewspaper;
  if (sourceLower.includes('inc')) return FaNewspaper;
  if (sourceLower.includes('harvard')) return FaBook;
  if (sourceLower.includes('entrepreneur')) return FaNewspaper;
  return FaGlobe;
};

// Relevance score colors
const getRelevanceColor = (score) => {
  if (score >= 4) return 'green';
  if (score >= 3) return 'yellow';
  return 'gray';
};

// CoverFlow Card Component - moved outside to prevent recreation on parent re-renders
const CoverFlowCard = memo(({ 
  item, 
  offset, 
  isDragging,
  previewImage,
  onOpenLink,
  onFeedback,
  onMarkRead,
  onSelect,
  cardBg,
  borderColor,
  subtleTextColor,
  primaryColor,
  linkColor,
  fallbackBg,
  hoverBg,
  selectedBorderColor,
}) => {
  const isRead = !!item.read_at;
  const isSaved = item.user_feedback === 'saved';
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Calculate vinyl coverflow 3D transforms
  const absOffset = Math.abs(offset);
  const direction = offset > 0 ? 1 : -1;
  const isCenter = offset === 0;
  const rotateY = isCenter ? 0 : direction * 50;
  const translateX = isCenter ? 0 : direction * (180 + (absOffset - 1) * 140);
  const translateZ = isCenter ? 100 : -80 - (absOffset * 20);
  const scale = isCenter ? 1 : 0.85 - (absOffset * 0.05);
  const cardOpacity = isCenter ? 1 : Math.max(0.3, 0.85 - absOffset * 0.2);

  return (
    <Box
      position="absolute"
      left="50%"
      top="50%"
      w={{ base: '200px', md: '230px', lg: '250px' }}
      h={{ base: '280px', md: '310px', lg: '330px' }}
      cursor={isCenter ? 'default' : 'pointer'}
      onClick={!isCenter ? onSelect : undefined}
      transition={isDragging ? 'none' : 'all 0.35s cubic-bezier(0.25, 0.1, 0.25, 1)'}
      style={{
        transform: `translateX(calc(-50% + ${translateX}px)) translateY(-50%) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
        zIndex: isCenter ? 100 : 50 - absOffset,
        opacity: cardOpacity,
        transformStyle: 'preserve-3d',
        transformOrigin: isCenter ? 'center center' : (direction > 0 ? 'left center' : 'right center'),
      }}
      _hover={!isCenter ? { opacity: Math.min(1, cardOpacity + 0.2) } : undefined}
    >
      <Card
        bg={cardBg}
        borderWidth={isCenter ? '2px' : '1px'}
        borderColor={isCenter ? selectedBorderColor : borderColor}
        borderRadius="lg"
        overflow="hidden"
        h="100%"
        boxShadow={isCenter 
          ? '0 25px 50px -12px rgba(0,0,0,0.4), 0 0 0 1px rgba(59,130,246,0.1)'
          : `0 ${10 - absOffset * 2}px ${25 - absOffset * 5}px -10px rgba(0,0,0,${0.35 - absOffset * 0.05})`
        }
        onMouseDown={isCenter ? (e) => e.stopPropagation() : undefined}
        onTouchStart={isCenter ? (e) => e.stopPropagation() : undefined}
      >
        {/* Preview Image */}
        <Box position="relative" h="50%" overflow="hidden" bg="gray.100">
          {previewImage && !imageError ? (
            <>
              {!imageLoaded && (
                <Skeleton position="absolute" top={0} left={0} w="100%" h="100%" />
              )}
              <Image
                src={previewImage}
                alt={item.title}
                w="100%"
                h="100%"
                objectFit="cover"
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                opacity={imageLoaded ? 1 : 0}
                transition="opacity 0.3s"
              />
            </>
          ) : (
            <Flex w="100%" h="100%" bg={fallbackBg} justify="center" align="center">
              <Icon as={getSourceIcon(item.source)} boxSize={10} color={primaryColor} opacity={0.6} />
            </Flex>
          )}
          {/* Source badge */}
          <Badge 
            position="absolute" 
            top={2} 
            left={2} 
            bg="blackAlpha.700"
            color="white"
            fontSize="xs"
            px={2}
            py={0.5}
          >
            {item.source}
          </Badge>
          {/* Relevance score */}
          <Badge 
            position="absolute" 
            top={2} 
            right={2} 
            colorScheme={getRelevanceColor(item.relevance_score)} 
            fontSize="xs"
          >
            <Flex align="center" gap={1}>
              <Icon as={FaStar} boxSize={2.5} />
              <Text as="span">{item.relevance_score}</Text>
            </Flex>
          </Badge>
        </Box>

        {/* Content */}
        <CardBody flex={1} display="flex" flexDirection="column" p={3}>
          <Text
            fontWeight="bold"
            fontSize={isCenter ? 'sm' : 'xs'}
            color={linkColor}
            noOfLines={2}
            lineHeight="short"
            mb={2}
            cursor={isCenter ? 'pointer' : 'default'}
            onClick={isCenter ? () => onOpenLink(item) : undefined}
            _hover={isCenter ? { color: 'blue.600' } : undefined}
          >
            {item.title}
          </Text>

          {/* Status badges */}
          <HStack spacing={1} mb={2} flexWrap="wrap">
            {isRead && (
              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                <Flex align="center" gap={1}>
                  <Icon as={FaCheckCircle} boxSize={2} />
                  <Text as="span">Read</Text>
                </Flex>
              </Badge>
            )}
            {isSaved && (
              <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                <Flex align="center" gap={1}>
                  <Icon as={FaBookmark} boxSize={2} />
                  <Text as="span">Saved</Text>
                </Flex>
              </Badge>
            )}
          </HStack>

          {/* Description - only for center card */}
          {isCenter && (
            <Text color={subtleTextColor} fontSize="xs" noOfLines={2} mb={2}>
              {item.relevance_reason}
            </Text>
          )}

          {/* Actions - only for center card */}
          {isCenter && (
            <Box mt="auto" pt={2}>
              <HStack justify="space-between">
                <Text fontSize="xs" color={subtleTextColor}>
                  {new Date(item.created_at).toLocaleDateString()}
                </Text>
                <HStack spacing={1}>
                  <Tooltip label={isSaved ? "Unsave" : "Save"}>
                    <IconButton
                      icon={<Icon as={isSaved ? FaBookmark : FaRegBookmark} />}
                      variant="ghost"
                      size="xs"
                      color={isSaved ? 'purple.500' : 'gray.400'}
                      onClick={(e) => {
                        e.stopPropagation();
                        onFeedback(item.reading_id, isSaved ? null : 'saved');
                      }}
                      aria-label="Save"
                    />
                  </Tooltip>
                  <Menu isLazy>
                    <MenuButton
                      as={IconButton}
                      icon={<Icon as={FaEllipsisV} />}
                      variant="ghost"
                      size="xs"
                      color="gray.400"
                      aria-label="More"
                      _hover={{ bg: hoverBg }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Portal>
                      <MenuList zIndex={1500}>
                        {!isRead && (
                          <MenuItem
                            icon={<Icon as={FaCheckCircle} color="green.500" />}
                            onClick={() => onMarkRead(item.reading_id)}
                          >
                            Mark as Read
                          </MenuItem>
                        )}
                        <MenuItem
                          icon={<Icon as={FaThumbsUp} color="green.500" />}
                          onClick={() => onFeedback(item.reading_id, 'helpful')}
                        >
                          Helpful
                        </MenuItem>
                        <MenuItem
                          icon={<Icon as={FaThumbsDown} color="red.500" />}
                          onClick={() => onFeedback(item.reading_id, 'not_helpful')}
                        >
                          Not Helpful
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    leftIcon={<Icon as={FaExternalLinkAlt} boxSize={2.5} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenLink(item);
                    }}
                  >
                    Read
                  </Button>
                </HStack>
              </HStack>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
});

CoverFlowCard.displayName = 'CoverFlowCard';

const ReadingListWidget = () => {
  const [readingItems, setReadingItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scopeFilter, setScopeFilter] = useState('all');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [previewImages, setPreviewImages] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const fetchedPreviewsRef = useRef(new Set());
  const carouselRef = useRef(null);
  const initialLoadDone = useRef(false);
  const hasSetInitialIndex = useRef(false);
  const navigate = useNavigate();

  // Theme colors - matched exactly with dashboard
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const primaryColor = useColorModeValue('blue.600', 'blue.400');
  const linkColor = useColorModeValue('gray.900', 'white');
  const carouselBg = useColorModeValue('linear-gradient(180deg, #f8fafc 0%, #edf2f7 100%)', 'linear-gradient(180deg, #1a202c 0%, #2d3748 100%)');
  const fallbackBg = useColorModeValue('blue.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBorderColor = useColorModeValue('blue.400', 'blue.300');

  // Fetch reading items - only on initial load
  const fetchReadingItems = useCallback(async () => {
    if (initialLoadDone.current) return;
    
    setIsLoading(true);
    try {
      const itemsData = await getReadingItems();
      setReadingItems(itemsData || []);
      setScopeFilter('all');
      initialLoadDone.current = true;
      logger.info('ReadingListWidget: Loaded reading items', { count: itemsData?.length });
    } catch (error) {
      logger.error('ReadingListWidget: Error loading reading items', error);
      setReadingItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReadingItems();
  }, [fetchReadingItems]);

  // Fetch preview images - removed previewImages from dependency to prevent loops
  useEffect(() => {
    if (!readingItems.length) return;
    
    const fetchPreviews = async () => {
      const itemsNeedingPreview = readingItems.filter(
        item => !item.preview_image && 
                !fetchedPreviewsRef.current.has(item.reading_id)
      );
      
      if (!itemsNeedingPreview.length) return;
      
      // Mark items as being fetched before starting
      itemsNeedingPreview.forEach(item => {
        fetchedPreviewsRef.current.add(item.reading_id);
      });
      
      // Fetch previews in parallel (limited to 5 at a time)
      const batchSize = 5;
      for (let i = 0; i < itemsNeedingPreview.length; i += batchSize) {
        const batch = itemsNeedingPreview.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(item => 
            fetchReadingItemPreview(item.reading_id).catch(() => ({ 
              reading_id: item.reading_id, 
              preview_image: null 
            }))
          )
        );
        
        // Update preview images state
        const newPreviews = {};
        results.forEach(result => {
          if (result.preview_image) {
            newPreviews[result.reading_id] = result.preview_image;
          }
        });
        
        if (Object.keys(newPreviews).length > 0) {
          setPreviewImages(prev => ({ ...prev, ...newPreviews }));
        }
      }
    };
    
    fetchPreviews();
  }, [readingItems]);

  // Filter reading items by scope (client-side)
  const filteredItems = useMemo(() => {
    return readingItems
      .filter(item => {
        if (!['business', 'location', 'industry'].includes(item.scope)) return false;
        if (scopeFilter !== 'all' && item.scope !== scopeFilter) return false;
        return true;
      })
      .sort((a, b) => {
        if (!a.read_at && b.read_at) return -1;
        if (a.read_at && !b.read_at) return 1;
        if (b.relevance_score !== a.relevance_score) {
          return b.relevance_score - a.relevance_score;
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });
  }, [readingItems, scopeFilter]);

  // Jump to the middle of the list on initial load
  useEffect(() => {
    if (filteredItems.length > 0 && !hasSetInitialIndex.current) {
      setSelectedIndex(Math.floor(filteredItems.length / 2));
      hasSetInitialIndex.current = true;
    }
  }, [filteredItems.length]);

  // Reset selectedIndex when filters change
  useEffect(() => {
    if (selectedIndex >= filteredItems.length && filteredItems.length > 0) {
      setSelectedIndex(filteredItems.length - 1);
    } else if (filteredItems.length === 0) {
      setSelectedIndex(0);
    }
  }, [filteredItems.length, selectedIndex]);

  // Navigate callbacks
  const goToPrevious = useCallback(() => {
    setSelectedIndex(prev => Math.max(0, prev - 1));
  }, []);

  const goToNext = useCallback(() => {
    setSelectedIndex(prev => Math.min(filteredItems.length - 1, prev + 1));
  }, [filteredItems.length]);

  // Drag handlers
  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    setDragStartX(clientX);
    setDragOffset(0);
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const offset = clientX - dragStartX;
    setDragOffset(offset);
  }, [isDragging, dragStartX]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const threshold = 50;
    if (dragOffset > threshold) {
      goToPrevious();
    } else if (dragOffset < -threshold) {
      setSelectedIndex(prev => Math.min(filteredItems.length - 1, prev + 1));
    }
    setDragOffset(0);
  }, [isDragging, dragOffset, goToPrevious, filteredItems.length]);

  // Handle opening link - update local state instead of refetching
  const handleOpenLink = useCallback((item) => {
    if (!item.read_at) {
      markReadingItemAsRead(item.reading_id).catch(() => {});
      // Update local state
      setReadingItems(prev => prev.map(i => 
        i.reading_id === item.reading_id 
          ? { ...i, read_at: new Date().toISOString() }
          : i
      ));
    }
    window.open(item.url, '_blank', 'noopener,noreferrer');
  }, []);

  // Handle feedback - update local state instead of refetching
  const handleFeedback = useCallback(async (readingId, feedback) => {
    try {
      await updateReadingItem(readingId, { user_feedback: feedback });
      // Update local state
      setReadingItems(prev => prev.map(i => 
        i.reading_id === readingId 
          ? { ...i, user_feedback: feedback }
          : i
      ));
    } catch (error) {
      logger.error('ReadingListWidget: Error updating feedback', error);
    }
  }, []);

  // Handle mark as read - update local state instead of refetching
  const handleMarkRead = useCallback(async (readingId) => {
    try {
      await markReadingItemAsRead(readingId);
      // Update local state
      setReadingItems(prev => prev.map(i => 
        i.reading_id === readingId 
          ? { ...i, read_at: new Date().toISOString() }
          : i
      ));
    } catch (error) {
      logger.error('ReadingListWidget: Error marking as read', error);
    }
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        overflow="hidden"
        boxShadow="sm"
        mb={6}
      >
        <CardBody p={4}>
          <Flex justify="space-between" align="center" mb={4}>
            <HStack spacing={2}>
              <Icon as={FaBookOpen} color={primaryColor} boxSize={5} />
              <Heading size="sm" fontWeight="semibold">Reading List</Heading>
            </HStack>
          </Flex>
          <Flex 
            justify="center" 
            align="center" 
            h="300px"
            bg={carouselBg}
            borderRadius="xl"
          >
            <Box textAlign="center">
              <Box
                w="40px"
                h="40px"
                borderRadius="full"
                border="3px solid"
                borderColor={primaryColor}
                borderTopColor="transparent"
                animation="spin 1s linear infinite"
                mx="auto"
                mb={3}
              />
              <Text color={subtleTextColor}>Loading Reading List...</Text>
            </Box>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  // Empty state
  if (!readingItems.length) {
    return null;
  }

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      boxShadow="sm"
      mb={6}
      _hover={{
        borderColor: 'blue.200',
        boxShadow: 'md',
      }}
      transition="all 0.2s"
    >
      <CardBody p={4}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={4}>
          <HStack spacing={3}>
            <Icon as={FaBookOpen} color={primaryColor} boxSize={5} />
            <Heading size="sm" fontWeight="semibold">Reading List</Heading>
          </HStack>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            rightIcon={<Icon as={FaChevronRight} boxSize={3} />}
            onClick={() => navigate('/playbook/reading')}
            fontWeight="medium"
          >
            View All
          </Button>
        </Flex>

        {/* Scope Tabs */}
        <Tabs
          variant="soft-rounded"
          colorScheme="blue"
          size="sm"
          mb={4}
          index={['all', 'business', 'location', 'industry'].indexOf(scopeFilter)}
          onChange={(index) => setScopeFilter(['all', 'business', 'location', 'industry'][index])}
        >
          <TabList flexWrap="wrap" gap={1}>
            <Tab fontSize="xs" px={3} py={1}>
              <Icon as={FaBook} mr={1} boxSize={2.5} />
              All
            </Tab>
            <Tab fontSize="xs" px={3} py={1}>
              <Icon as={FaStar} mr={1} boxSize={2.5} />
              Business
            </Tab>
            <Tab fontSize="xs" px={3} py={1}>
              <Icon as={FaMapMarkerAlt} mr={1} boxSize={2.5} />
              Local
            </Tab>
            <Tab fontSize="xs" px={3} py={1}>
              <Icon as={FaIndustry} mr={1} boxSize={2.5} />
              Industry
            </Tab>
          </TabList>
        </Tabs>

        {/* CoverFlow Carousel */}
        {filteredItems.length > 0 ? (
          <Box mb={4}>
            <Box
              ref={carouselRef}
              position="relative"
              h={{ base: '400px', md: '430px', lg: '450px' }}
              overflow="hidden"
              borderRadius="xl"
              bg={carouselBg}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
              cursor={isDragging ? 'grabbing' : 'grab'}
              userSelect="none"
              style={{
                perspective: '1000px',
                perspectiveOrigin: '50% 50%',
              }}
            >
              {/* 3D Stage */}
              <Box
                position="relative"
                w="100%"
                h="100%"
                style={{
                  transformStyle: 'preserve-3d',
                }}
              >
                {filteredItems.map((item, index) => {
                  const offset = index - selectedIndex;
                  if (Math.abs(offset) > 4) return null;
                  
                  return (
                    <CoverFlowCard
                      key={item.reading_id}
                      item={item}
                      offset={offset}
                      isDragging={isDragging}
                      previewImage={item.preview_image || previewImages[item.reading_id]}
                      onOpenLink={handleOpenLink}
                      onFeedback={handleFeedback}
                      onMarkRead={handleMarkRead}
                      onSelect={() => setSelectedIndex(index)}
                      cardBg={cardBg}
                      borderColor={borderColor}
                      subtleTextColor={subtleTextColor}
                      primaryColor={primaryColor}
                      linkColor={linkColor}
                      fallbackBg={fallbackBg}
                      hoverBg={hoverBg}
                      selectedBorderColor={selectedBorderColor}
                    />
                  );
                })}
              </Box>

              {/* Navigation Arrows */}
              <IconButton
                icon={<Icon as={FaChevronLeft} boxSize={5} />}
                position="absolute"
                left={2}
                top="50%"
                transform="translateY(-50%)"
                zIndex={200}
                size="md"
                variant="solid"
                colorScheme="blue"
                borderRadius="full"
                isDisabled={selectedIndex === 0}
                onClick={goToPrevious}
                aria-label="Previous article"
                boxShadow="lg"
                _hover={{ transform: 'translateY(-50%) scale(1.1)' }}
                opacity={selectedIndex === 0 ? 0.3 : 0.9}
              />
              <IconButton
                icon={<Icon as={FaChevronRight} boxSize={5} />}
                position="absolute"
                right={2}
                top="50%"
                transform="translateY(-50%)"
                zIndex={200}
                size="md"
                variant="solid"
                colorScheme="blue"
                borderRadius="full"
                isDisabled={selectedIndex >= filteredItems.length - 1}
                onClick={goToNext}
                aria-label="Next article"
                boxShadow="lg"
                _hover={{ transform: 'translateY(-50%) scale(1.1)' }}
                opacity={selectedIndex >= filteredItems.length - 1 ? 0.3 : 0.9}
              />

              {/* Edge fade overlays */}
              <Box
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                w={{ base: '60px', md: '100px', lg: '140px' }}
                bgGradient="linear(to-r, #f8fafc 0%, #f8fafc 20%, transparent 100%)"
                pointerEvents="none"
                zIndex={150}
                borderLeftRadius="xl"
              />
              <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                w={{ base: '60px', md: '100px', lg: '140px' }}
                bgGradient="linear(to-l, #edf2f7 0%, #edf2f7 20%, transparent 100%)"
                pointerEvents="none"
                zIndex={150}
                borderRightRadius="xl"
              />
            </Box>

            {/* Position Indicator */}
            <Flex justify="center" mt={3} gap={1}>
              {filteredItems.slice(0, Math.min(12, filteredItems.length)).map((_, index) => (
                <Box
                  key={index}
                  w={index === selectedIndex ? '20px' : '6px'}
                  h="6px"
                  borderRadius="full"
                  bg={index === selectedIndex ? 'blue.500' : 'gray.300'}
                  cursor="pointer"
                  onClick={() => setSelectedIndex(index)}
                  transition="all 0.3s ease"
                  _hover={{ bg: index === selectedIndex ? 'blue.600' : 'gray.400' }}
                />
              ))}
              {filteredItems.length > 12 && (
                <Text fontSize="xs" color={subtleTextColor} ml={1}>
                  +{filteredItems.length - 12} more
                </Text>
              )}
            </Flex>

            {/* Navigation hint */}
            <Text textAlign="center" fontSize="xs" color={subtleTextColor} mt={2}>
              Swipe or click arrows to navigate
            </Text>
          </Box>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            p={6}
            bg={carouselBg}
            borderRadius="xl"
            mb={4}
            h="200px"
          >
            <Icon as={FaBookOpen} boxSize={10} color="gray.300" mb={3} />
            <Text color={subtleTextColor} fontSize="sm" mb={2}>No articles match this filter</Text>
            <Button
              variant="link"
              colorScheme="blue"
              size="sm"
              onClick={() => setScopeFilter('all')}
            >
              Show all articles
            </Button>
          </Flex>
        )}

        {/* Footer link */}
        <Flex 
          justify="center" 
          pt={3} 
          borderTopWidth="1px" 
          borderColor={borderColor}
        >
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            leftIcon={<Icon as={FaBookOpen} boxSize={3} />}
            onClick={() => navigate('/playbook/reading')}
          >
            Browse Full Reading List
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default ReadingListWidget;
